import type { App, Component } from 'vue'
import type { RouteLocationRaw } from 'vue-router'
import { adminGuard, authorizedGuard } from '@/auth'
import { registerComponents } from './components'
import { organizationGuard } from './guards/organization'
import { singleOrganizationGuard } from './guards/single-organization'
import { useApiFailureStore } from './store'

export * from './composables/organization'
export * from './composables/organization-meta'
export * from './guards/organization'
export * from './guards/single-organization'
export * from './store'

export type Crumb = {
  labelKey: string
  to?: RouteLocationRaw
}

// A sidebar entry is the route itself: the label is its `head.titleKey` and the target is
// its name, so only what a route cannot already say lives here.
export type NavEntry = {
  icon: Component
  order?: number
  section?: string
  trailing?: Component
  below?: Component
}

declare module 'vue-router' {
  interface RouteMeta {
    bare?: boolean
    crumbs?: Crumb[]
    nav?: NavEntry
    setup?: boolean
  }
}

export const createLayout = () => ({
  install: (app: App) => {
    registerComponents(app)

    // Instantiated for its side effect: the store braids in the strand that raises `DgFailure`,
    // and it has to exist before the first request rather than on the notice's first render.
    app.runWithContext(() => useApiFailureStore())

    const router = app.getRouter()

    router.addRoute({
      path: '/organizations',
      name: 'organizations',
      beforeEnter: [authorizedGuard, singleOrganizationGuard],
      meta: { head: { titleKey: 'organizations.pickerTitle' } },
      component: () => import('./pages/OrganizationPickerPage.vue')
    })

    // `/` cannot know which organization, so it asks (FR-A21).
    router.addRoute({
      path: '/',
      name: 'home',
      redirect: { name: 'organizations' },
      beforeEnter: authorizedGuard
    })

    // Every feature route hangs off this one, so a URL cannot omit the organization
    // (`ARCHITECTURE.md` §3.1).
    router.addRoute({
      path: '/organizations/:organizationId',
      name: 'organization',
      beforeEnter: [authorizedGuard, organizationGuard],
      component: () => import('./pages/LayoutPage.vue'),
      redirect: { name: 'dashboard' },
      children: []
    })

    router.addRoute('organization', {
      path: 'settings',
      name: 'settings',
      meta: {
        head: { titleKey: 'settings.title' },
        crumbs: [{ labelKey: 'nav.workspace', to: { name: 'dashboard' } }, { labelKey: 'settings.title' }]
      },
      component: () => import('./pages/SettingsPage.vue'),
      children: []
    })

    router.addRoute('settings', {
      path: 'workspace',
      name: 'settings-workspace',
      meta: { head: { titleKey: 'settings.workspace.tab' } },
      component: () => import('./components/WorkspacePanel.vue')
    })

    // FR-A14's reviewer surface; the API checks `is_admin` per request too.
    router.addRoute({
      path: '/admin',
      name: 'admin',
      beforeEnter: [authorizedGuard, adminGuard],
      component: () => import('./pages/LayoutPage.vue'),
      redirect: { name: 'admin-verification-queue' },
      children: []
    })

    router.addRoute('admin', {
      path: 'settings',
      name: 'admin-settings',
      meta: {
        head: { titleKey: 'settings.title' },
        crumbs: [{ labelKey: 'nav.admin', to: { name: 'admin' } }, { labelKey: 'settings.title' }]
      },
      component: () => import('./pages/SettingsPage.vue'),
      children: []
    })
  }
})
